import { useEffect, useState } from "react";
import { Copy, Check, X, Share2, Code2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* Share modal that gives the canonical share URL (via /api/share/:id so
 * OG tags render on TikTok / IG / X / iMessage / WhatsApp / Telegram), plus
 * an iframe embed snippet for sites and a Web Share API quick action.
 */
export default function ShareModal({ video, onClose }) {
  const [copied, setCopied] = useState(null);
  // INTERIM: share surface points at the backend SSR endpoint so TikTok / IG /
  // X / iMessage / WhatsApp / Telegram crawl per-video OG + VideoObject in the
  // initial HTTP response. (Emergent's platform-owned edge worker currently
  // routes only /api/* to the backend — pending ticket to add /watch/* edge
  // rule, after which we flip this back to the pretty /watch/{id} URL.
  // Humans landing on /api/ssr/watch/{id} get the browser URL rewritten to
  // /watch/{id} by an inline script before React mounts.)
  const shareUrl = `${BACKEND_URL}/api/ssr/watch/${video.id}`;
  const watchUrl = `${BACKEND_URL}/api/ssr/watch/${video.id}`;
  const embedSnippet = `<iframe src="${BACKEND_URL}/embed/${video.id}" width="${video.type === "short" ? 360 : 720}" height="${video.type === "short" ? 640 : 405}" frameborder="0" allow="autoplay; fullscreen; encrypted-media; picture-in-picture" allowfullscreen></iframe>`;

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success("Copied");
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };

  const native = async () => {
    if (!navigator.share) return toast.error("Native share unavailable");
    try {
      await navigator.share({ title: video.title, text: video.description || "", url: shareUrl });
    } catch { /* user cancelled */ }
  };

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const targets = [
    { key: "x", label: "X / Twitter", url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(video.title)}&url=${encodeURIComponent(shareUrl)}` },
    { key: "fb", label: "Facebook", url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
    { key: "wa", label: "WhatsApp", url: `https://wa.me/?text=${encodeURIComponent(video.title + " — " + shareUrl)}` },
    { key: "tg", label: "Telegram", url: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(video.title)}` },
    { key: "rd", label: "Reddit", url: `https://www.reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(video.title)}` },
    { key: "em", label: "Email", url: `mailto:?subject=${encodeURIComponent(video.title)}&body=${encodeURIComponent(shareUrl)}` },
  ];

  return (
    <div data-testid="share-modal" onClick={onClose} className="fixed inset-0 z-[60] bg-black/85 backdrop-blur flex items-center justify-center p-4">
      <div onClick={(e) => e.stopPropagation()} className="rounded-2xl bg-[#0a0a0a] border border-white/10 max-w-lg w-full p-6 fade-up aura-border-glow">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-[#FFB800]" />
            <h3 className="text-lg font-bold text-white">Share this drop</h3>
          </div>
          <button data-testid="share-close" onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="mt-4 grid grid-cols-3 sm:grid-cols-6 gap-2">
          {targets.map((t) => (
            <a
              key={t.key}
              data-testid={`share-target-${t.key}`}
              href={t.url}
              target="_blank"
              rel="noreferrer"
              className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-zinc-900/60 border border-white/10 hover:border-[#FF6B00]/50 transition-colors"
            >
              <span className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FF6B00] to-[#FFB800] text-black font-black flex items-center justify-center text-xs">
                {t.label.slice(0, 2).toUpperCase()}
              </span>
              <span className="text-[10px] uppercase tracking-widest text-zinc-400">{t.label}</span>
            </a>
          ))}
        </div>

        <div className="mt-5">
          <label className="text-[10px] uppercase tracking-widest text-zinc-500">Share link (rich preview)</label>
          <div className="mt-1 flex items-stretch gap-2">
            <input
              data-testid="share-url"
              readOnly
              value={shareUrl}
              className="flex-1 h-11 px-3 bg-zinc-900/70 border border-white/10 rounded-lg text-white text-sm font-mono"
              onFocus={(e) => e.target.select()}
            />
            <button data-testid="share-copy" onClick={() => copy(shareUrl, "url")} className="aura-button px-4 rounded-lg text-xs uppercase tracking-widest inline-flex items-center gap-1">
              {copied === "url" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} Copy
            </button>
          </div>
          <p className="mt-1 text-[10px] text-zinc-500">Optimized for socials — shows full preview card on X, IG, iMessage, WhatsApp, Telegram, Discord.</p>
        </div>

        <div className="mt-4">
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1.5"><Code2 className="w-3 h-3" /> Embed</label>
          <div className="mt-1 flex items-stretch gap-2">
            <input
              data-testid="embed-snippet"
              readOnly
              value={embedSnippet}
              className="flex-1 h-11 px-3 bg-zinc-900/70 border border-white/10 rounded-lg text-zinc-300 text-xs font-mono truncate"
              onFocus={(e) => e.target.select()}
            />
            <button data-testid="embed-copy" onClick={() => copy(embedSnippet, "embed")} className="px-4 rounded-lg bg-zinc-900 border border-white/10 hover:border-white/30 text-xs uppercase tracking-widest text-white inline-flex items-center gap-1">
              {copied === "embed" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500">
          <button data-testid="share-canonical" onClick={() => copy(watchUrl, "watch")} className="hover:text-[#FFB800]">{copied === "watch" ? "Watch link copied" : "Copy direct watch URL"}</button>
          {typeof navigator !== "undefined" && navigator.share && (
            <button data-testid="share-native" onClick={native} className="text-[#FFB800] hover:text-white">Native share →</button>
          )}
        </div>
      </div>
    </div>
  );
}
